import { GetPostQuery } from "@/__generated__/graphql";
import { useGSAP } from "@gsap/react";
import className from "classnames/bind";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
  forwardRef,
  Ref,
  SyntheticEvent,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import styles from "./CantUnhearItBackground.module.scss";
import { useViewSize } from "@/providers/ViewSizeContext";
import { Image } from "@components";
import { dedupeLineBreaks } from "@/core/utils";
import { usePageWrapperData } from "@/providers/PageWrapperContext";
import { useClientMount } from "@/providers/ClientMountProvider";

type TracksType =
  GetPostQuery["rhPost"]["featureData"]["sonicConnections"]["tracks"];
type CantUnhearItBackgroundProps = {
  tracks: TracksType;
  onAnimateIn: () => void;
};
const cx = className.bind(styles);

export interface CantUnhearItBackgroundRefs {
  timeline: gsap.core.Timeline | null;
  containerEl: HTMLDivElement | null;
}

const CantUnhearItBackground = forwardRef<
  CantUnhearItBackgroundRefs,
  CantUnhearItBackgroundProps
>(({ tracks, onAnimateIn }, ref) => {
  const [playDisc, setPlayDisc] = useState(false);
  const { labels } = usePageWrapperData(); 
  const [timelineReady, setTimelineReady] = useState(false);
  const imagesRef = useRef<HTMLDivElement>(null);
  const innerImagesRef = useRef<HTMLImageElement[]>([]);
  const blackOverRef = useRef<HTMLDivElement>(null);
  const flatImagesEl = useRef<HTMLDivElement>(null);
  const discImagesEl = useRef<HTMLDivElement>(null);
  const titlesEl = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { trackLabel, releaseYearLabel } = labels || {};
  const timeline = useRef<gsap.core.Timeline>(null);
  const discSpeed = useRef<number>(0);
  const { isDesktop, isReady } = useViewSize();
  const { mounted } = useClientMount();
  const totalImagesLoaded = useRef(0);

  const dedupedTracks = useMemo(() => {
    return tracks.filter(
      (value, index, self) =>
        self.findIndex(
          (el) =>
            value.heroImage.node.mediaItemUrl === el.heroImage.node.mediaItemUrl
        ) === index
    );
  }, [tracks]);

  useImperativeHandle(
    ref,
    () => ({
      timeline: timeline.current,
      containerEl: containerRef.current,
    }),
    [timelineReady]
  );

  const createTimeline = useCallback(() => {
    if (!isDesktop) {
      onAnimateIn();
      return;
    }
    timeline.current = new gsap.core.Timeline({
      paused: true,
      onUpdate: () => {
        if (timeline.current.time() < timeline.current.labels["fadeInDisc"]) {
          setPlayDisc(false);
        } else if (
          timeline.current.time() >= timeline.current.labels["fadeInDisc"]
        ) {
          setPlayDisc(true);
        }
      },
    });
    timeline.current.addLabel("stackImages");

    timeline.current.to(
      imagesRef.current.children,
      {
        x: "-50%",
        rotationY: 0,
      },
      "stackImages"
    );

    timeline.current.to(
      imagesRef.current.children,
      {
        scaleX: 1,
        scaleY: 1,
      },
      "stackImages"
    );

    timeline.current.fromTo(
      [discImagesEl.current, titlesEl.current, flatImagesEl.current],
      {
        opacity: 0,
      },
      { opacity: 1, duration: 0.1 },
      "fadeInDisc"
    );

    timeline.current.fromTo(
      discImagesEl.current,
      {
        filter: "drop-shadow(0 0px 0px rgba(0, 0, 0, 0.3))",
      },
      {
        filter: "drop-shadow(0 12px 30px rgba(0, 0, 0, 0.9))",
        onStart: () => {
          gsap.set(discImagesEl.current.querySelectorAll("img"), {
            rotation: 0,
          });
        },
      },
      "fadeInDisc"
    );

    timeline.current.fromTo(
      discSpeed,
      {
        current: 0,
      },
      { current: 1, ease: "linear" },
      "fadeInDisc"
    );

    timeline.current.fromTo(
      titlesEl.current,
      {
        clipPath: "circle(0%)",
      },
      { clipPath: "circle(100%)" },
      "fadeInDisc"
    );

    timeline.current.fromTo(
      titlesEl.current.children,
      {
        scale: 0.5,
      },
      { scale: 1 },
      "fadeInDisc"
    );

    Array.from(flatImagesEl.current.children).forEach((item, index) => {
      if (index === 0) {
        timeline.current.fromTo(
          item,
          {
            opacity: 0,
          },
          { opacity: 1, duration: 0 },
          `track-${index}`
        );
      }
      timeline.current.fromTo(
        item,
        {
          y: index === 0 ? 0 : "100%",
          clipPath:
            index === 0 ? "rect(0% 100% 100% 0%)" : "rect(0% 100% 0% 0%)",
          transformOrigin: "bottom",
        },
        { y: 0, clipPath: "rect(0% 100% 100% 0%)" },
        `track-${index}`
      );

      timeline.current.fromTo(
        discImagesEl.current.children[index],
        {
          clipPath:
            index === 0 ? "rect(0% 100% 100% 0%)" : "rect(100% 100% 100% 0%)",
        },
        { clipPath: "rect(0% 100% 100% 0%)" },
        `track-${index}-=0.01`
      );

      timeline.current.to(
        titlesEl.current.children,
        { y: `-${index * titlesEl.current.getBoundingClientRect().height}` },
        `track-${index}`
      );
    });
    setTimelineReady(true); // Signal that timeline is ready
    onAnimateIn();
  }, [isDesktop, onAnimateIn]);

  useGSAP(() => {
    if (!mounted || !isReady) return;
    if (imagesRef.current) {
      gsap.registerPlugin(ScrollTrigger);

      // first, reverts zOrder
      const imagesArray = gsap.utils.toArray(imagesRef.current.children);
      Array.from(imagesArray)
        .reverse()
        .forEach((el: Element, index) => {
          gsap.set(el, { zIndex: index });
        });

      const total = imagesRef.current.children.length;
      const firstImage = imagesRef.current.children[0];
      gsap.set(firstImage, {
        marginLeft: isDesktop ? 0 : "-6vh",
        rotationY: -45,
        duration: 1,
        ease: "power3.out",
      });

      const otherImages = imagesArray.slice(1, total);
      gsap.set(otherImages, {
        rotationY: -45,
      });

      let startX = gsap.getProperty(firstImage, "x") as number;
      if (!isDesktop && typeof window !== "undefined") {
        startX -= window.innerHeight * 0.1;
      }
      let startScale = isDesktop ? 0.8 : 1;
      let diff = 350 / imagesArray.length;

      otherImages.forEach((el: Element, index) => {
        const endX = startX + diff;
        gsap.fromTo(
          el,
          {
            x: endX,
            scale: startScale - 0.1,
          },
          {
            x: endX,
            scale: startScale - 0.1,
            ease: "power3.out",
            delay: 0.3 + index * 0.1,
            duration: 0.8,
            onComplete:
              index === otherImages.length - 1 ? createTimeline : null,
          }
        );

        startX = endX;
        startScale = startScale - 0.1;
      });
    }
  }, [mounted, isReady, imagesRef, blackOverRef]);

  const handleImageLoad = useCallback(
    (event: SyntheticEvent<HTMLImageElement>) => {
      gsap.set(event.target, { opacity: 1 });
      totalImagesLoaded.current++;

      if (
        totalImagesLoaded.current === dedupedTracks.length &&
        dedupedTracks.length > 0
      ) {
        gsap.to(blackOverRef.current, {
          opacity: 0,
          ease: "linear",
          duration: 0.5,
          delay: 0.2,
        });
        if (typeof window !== "undefined") {
          window.scrollTo(0, 0);
        }
      }
    },
    [dedupedTracks.length]
  );

  useEffect(() => {
    if (innerImagesRef.current) {
      innerImagesRef.current.forEach((img: HTMLImageElement) => {
        handleImageLoad({
          target: img,
        } as unknown as SyntheticEvent<HTMLImageElement>);
      });
    }
  }, [handleImageLoad]);

  useEffect(() => {
    if (!mounted || !isReady || !isDesktop) return;

    const rotateImages = () => {
      if (discImagesEl?.current) {
        gsap.set(discImagesEl.current.querySelectorAll("img"), {
          rotate: `+=${discSpeed.current * 0.5}`,
        });
      }
    };

    gsap.ticker.add(rotateImages);
    return () => {
      gsap.ticker.remove(rotateImages);
    };
  }, [mounted, isReady, discSpeed, isDesktop]);

  return (
    <div
      className={cx("cant-unhear-it__background-container")}
      ref={containerRef}
    >
      <div className={cx("cant-unhear-it__background-images")} ref={imagesRef}>
        {dedupedTracks.map((track, index) => (
          <div
            key={`image-${index}`}
            className={cx("cant-unhear-it__background-image")}
          >
            <Image
              ref={(el) => {
                innerImagesRef.current[index] = el;
              }}
              image={track.heroImage}
              style={{ opacity: 0 }}
              onLoad={handleImageLoad}
            />
          </div>
        ))}
      </div>

      <div className={cx("cant-unhear-it__black-over")} ref={blackOverRef} />

      <div
        className={cx("cant-unhear-it__flat-images")}
        ref={flatImagesEl}
        style={{ opacity: 0 }}
      >
        {tracks.map((track, index) => (
          <div
            key={`flat-${index}`}
            className={cx("cant-unhear-it__flat-image")}
          >
            <Image image={track.heroImage} />
          </div>
        ))}
      </div>

      <div
        className={cx("cant-unhear-it__disc")}
        ref={discImagesEl}
        style={{ opacity: 0 }}
      >
        {tracks.map((track, index) => (
          <div
            key={`disc-${index}`}
            className={cx("cant-unhear-it__disc-image")}
          >
            <div
              className={cx("cant-unhear-it__disc-image", "circle", {
                "cant-unhear-it__disc-image--animate": playDisc,
              })}
            >
              <Image image={track.heroImage} />
            </div>
          </div>
        ))}
      </div>

      <div
        className={cx("cant-unhear-it__titles")}
        ref={titlesEl}
        style={{ opacity: 0 }}
      >
        {tracks.map((track, index) => (
          <div key={`disc-${index}`} className={cx("cant-unhear-it__title")}>
            <h3 className={cx("track-name")}>
              {trackLabel}
              <br />
              <span
                dangerouslySetInnerHTML={{
                  __html: dedupeLineBreaks(track.trackName),
                }}
              />
            </h3>
            <p
              className={cx("artist-name")}
              dangerouslySetInnerHTML={{
                __html: dedupeLineBreaks(track.artistName),
              }}
            />
            <p className={cx("release-year")}>
              {releaseYearLabel}
              <br />
              {track.releaseYear}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
});

export default CantUnhearItBackground;
